import { useCallback, useMemo, useRef } from 'react';
import { useGetIdentity } from '@refinedev/core';
import { useNavigate } from 'react-router';
import type { IERPUser } from '@admin-panel/types';
import { useGlobalContext } from '@admin-panel/hooks';
import {
  ALL_FACILITIES,
  alertDestination,
  canOpenPackagePages,
  hubPath,
  linkOf,
  matchesSelection,
  shouldShowFacility,
  type HubAlert,
  type HubAlertLink,
} from '../hubAlertRules';
import type { FacilityBacklog } from '../hubBriefingRules';

const here = () => window.location.pathname + window.location.search;

/**
 * The facility an alert (or a waiting line) just switched to, so that switch is not announced as
 * "new alerts here": the operator is already on that alert's page. Taken once.
 */
let alertSwitch: { facilityId: number; at: number } | null = null;
export const takeAlertSwitch = (facilityId: number, now = Date.now()) => {
  const switched =
    alertSwitch !== null && alertSwitch.facilityId === facilityId && now - alertSwitch.at < 2000;
  alertSwitch = null;
  return switched;
};

/**
 * Where an alert or a waiting line leads: open its page, then switch to its facility (when it is
 * one of the operator's and not already shown). Shared by notification taps and the alerts drawer.
 * Each returns whether Hub went there: a task with unsaved work can refuse to be left (packing
 * guard), and then neither the page nor the facility changes.
 */
export const useAlertNavigation = () => {
  const { data: user } = useGetIdentity<IERPUser>();
  const { warehouseId, setWarehouseId } = useGlobalContext();
  const navigate = useNavigate();
  const roles = user?.roles;
  const userFacilityIds = user?.adminUserAttributes?.warehouseIds;
  const selectionRef = useRef(warehouseId);
  selectionRef.current = warehouseId;

  const userCanSelect = useCallback(
    (facilityId: number | null) =>
      facilityId !== null && Boolean(userFacilityIds?.includes(facilityId)),
    [userFacilityIds],
  );

  const switchToFacility = useCallback(
    (facilityId: number | null) => {
      const selection = selectionRef.current;
      if (userCanSelect(facilityId) && selection !== ALL_FACILITIES && selection !== facilityId) {
        alertSwitch = { facilityId: facilityId as number, at: Date.now() };
        setWarehouseId(facilityId as number);
      }
    },
    [setWarehouseId, userCanSelect],
  );

  /** Navigates and tells whether Hub is now on that page (a guard may have refused). */
  const go = useCallback(
    (path: string, replace: boolean) => {
      navigate(path, { replace });
      const target = new URL(path, window.location.origin);
      return here() === target.pathname + target.search;
    },
    [navigate],
  );

  const openLink = useCallback(
    (link: HubAlertLink | null, url: string, replace = false) => {
      if (!link) {
        return go(hubPath(url, window.location.origin), replace);
      }
      const went = go(alertDestination(link, url, roles, window.location.origin), replace);
      if (went) switchToFacility(link.facilityId);
      return went;
    },
    [go, roles, switchToFacility],
  );

  const openAlert = useCallback(
    (alert: HubAlert) => openLink(linkOf(alert), alert.url),
    [openLink],
  );

  const startWork = useCallback(
    (backlog: FacilityBacklog, url: string) => {
      const went = go(url, false);
      if (went) switchToFacility(backlog.facilityId);
      return went;
    },
    [go, switchToFacility],
  );

  const canOpenPackages = useMemo(() => canOpenPackagePages(roles), [roles]);
  /** Names the facility on alerts when it is not obvious from the screen. */
  const showFacility = shouldShowFacility(warehouseId, userFacilityIds?.length ?? 0);
  const isElsewhere = useCallback(
    (alert: HubAlert) => !matchesSelection(alert.facilityId, warehouseId),
    [warehouseId],
  );

  return {
    openLink,
    openAlert,
    startWork,
    userCanSelect,
    canOpenPackages,
    showFacility,
    isElsewhere,
  };
};
